import * as Colyseus from "colyseus.js";
import { ACTION } from "@hupper/shared/protocol";
import type {
  BuyWeaponMsg,
  ChatBroadcastMsg,
  ChatMsg,
  DropWeaponMsg,
  FriendRequestMsg,
  FriendResultMsg,
  FriendStatusMsg,
  GrenadeDetonateBroadcastMsg,
  GrenadeBounceBroadcastMsg,
  GrenadeStateBroadcastMsg,
  GrenadeThrowMsg,
  GroundItemsStateBroadcastMsg,
  GlassBreakBroadcastMsg,
  HitBroadcastMsg,
  KillBroadcastMsg,
  FlashBlindMsg,
  MatchEndBroadcastMsg,
  PickupWeaponMsg,
  ReportPlayerMsg,
  ReportReason,
  RoundEndBroadcastMsg,
  WeaponEquipBroadcastMsg,
  WeaponEquipMsg,
  WeaponFireBroadcastMsg,
  ZoneStateBroadcastMsg,
} from "@hupper/shared/protocol";

/**
 * Server-synced player state shape — mirrors the snapshot message
 * broadcast by services/game-server/src/rooms/FpsArena.ts at 32Hz.
 */
export interface RemotePlayerState {
  sessionId: string;
  name: string;
  /** Public avatar image URL (uploaded photo), or "" / undefined →
   *  the scoreboard renders the generated colored fallback. */
  avatarUrl?: string;
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  health: number;
  /** Kevlar armor value (0-100). */
  armor?: number;
  /** True when the player has a helmet (head protection). */
  helmet?: boolean;
  /** Round-trip ping in ms (server-measured), for the scoreboard. */
  ping?: number;
  /** Match-long kill / death tally (scoreboard). */
  kills?: number;
  deaths?: number;
  /** Match-long damage dealt to the opponent (result screen). */
  damageDealt?: number;
  weaponId: string;
  lastProcessedInputSeq: number;
  team?: "ct" | "t";
  money?: number;
  ownedWeapons?: string[];
  /** True when peer is holding Ctrl — drives lower silhouette client-side. */
  crouching?: boolean;
  /** True when peer is moving with Shift held — reserved for footstep audio. */
  walking?: boolean;
  /** Bullets in the equipped weapon's magazine. */
  magAmmo?: number;
  /** Reserve ammo (extra mags) for the equipped weapon. */
  reserveAmmo?: number;
  /** True while a reload animation is in progress. */
  reloading?: boolean;
  /** Server tick at which the current reload completes (0 = none). */
  reloadEndTick?: number;
  /** CS:GO tagging multiplier (1 = full speed); drops when shot/knifed. */
  velocityModifier?: number;
  /**
   * World-state passenger (not per-player): the breakable glass pane's
   * broken state, stamped onto the local player's snapshot so
   * `onLocalSnapshot` consumers can self-heal the rendered pane. Rides
   * along the same way the HUD/world fields do.
   */
  glassBroken?: boolean;
  /**
   * Server tick this snapshot was captured at — stamped client-side in
   * `diffSnapshot` from the enclosing snapshot message. Drives the remote
   * players' snapshot-buffer interpolation (`RemotePlayer`).
   */
  tick?: number;
}

export interface MatchHudState {
  phase: string;
  ctScore: number;
  tScore: number;
  roundNumber: number;
  roundsToWin: number;
  buySecondsLeft?: number;
  buyEndsAtTick?: number;
  /** Sprint 6 — seconds left in live phase. */
  liveSecondsLeft?: number;
  /** Seconds left in the pre-buy VS intro (only set while `phase === "intro"`). */
  introSecondsLeft?: number;
  /** Seconds left in the halftime side-swap cinematic (only set while
   *  `phase === "halftime"`). */
  halftimeSecondsLeft?: number;
  /** Sprint 6 — round at which halftime swaps sides (MR12 = 12). */
  halftimeAtRound?: number;
  halftimeSwapped?: boolean;
}

interface SnapshotMsg {
  tick: number;
  phase: string;
  /** True when the server supports the asset-load handshake (`client_ready`).
   *  Absent on older servers — which KICK on unregistered message types
   *  (colyseus WS_CLOSE_WITH_ERROR in production), so the client must never
   *  send `client_ready` before seeing this flag. */
  loadGate?: boolean;
  ctScore?: number;
  tScore?: number;
  roundNumber?: number;
  roundsToWin?: number;
  buyEndsAtTick?: number;
  buySecondsLeft?: number;
  liveSecondsLeft?: number;
  introSecondsLeft?: number;
  halftimeSecondsLeft?: number;
  halftimeAtRound?: number;
  halftimeSwapped?: boolean;
  /** World-state: true once the breakable glass pane has shattered this round. */
  glassBroken?: boolean;
  players: RemotePlayerState[];
}

/** Server → survivor when the opponent drops and a grace window opens. */
export interface OpponentPausedMsg {
  sessionId: string;
  graceSec: number;
  dropCount: number;
}

export interface InputFrame {
  seq: number;
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  crouch: boolean;
  walk: boolean;
  /** Absolute look angles in DEGREES (client-authoritative aim). The server
   *  sets player.yaw/pitch directly from these, which makes a client/server
   *  yaw base mismatch impossible — the delta-rebase desync after respawn
   *  (walk veers + shots miss the crosshair) is structurally eliminated. */
  yaw: number;
  pitch: number;
  /** Mouse delta this frame in DEGREES. LEGACY/transitional: kept only so an
   *  not-yet-redeployed server can still integrate deltas during the deploy
   *  skew window. The current server prefers the absolute yaw/pitch above. */
  dPitch: number;
  dYaw: number;
  actionFlags: number;
}

export type PlayerEvent = (player: RemotePlayerState, sessionId: string) => void;
export type MatchHudEvent = (hud: MatchHudState) => void;
export type RoundEndEvent = (msg: RoundEndBroadcastMsg) => void;
export type VoidEvent = () => void;
export type WeaponEquipEvent = (msg: WeaponEquipBroadcastMsg) => void;

/**
 * GameClient — wraps colyseus.js for the FPS Arena room.
 *
 * Uses plain-message snapshots (not schema reactivity) because schema
 * sync had reliability issues across the colyseus.js 0.16 +
 * @colyseus/schema 3.x version matrix in our setup. Messages are
 * version-stable and trivially debuggable.
 *
 * The server pushes a "snapshot" message at 32Hz containing all players'
 * authoritative state. We diff against a known Map to emit add / change
 * / remove events to consumers (e.g. RemotePlayerManager).
 */
export class GameClient {
  private client: Colyseus.Client;
  private room: Colyseus.Room | null = null;

  /** Event emitter — multiple subscribers per event. */
  private addCbs: PlayerEvent[] = [];
  private changeCbs: PlayerEvent[] = [];
  private removeCbs: PlayerEvent[] = [];
  private hudCbs: MatchHudEvent[] = [];
  private roundEndCbs: RoundEndEvent[] = [];
  private liveRoundCbs: VoidEvent[] = [];
  private weaponEquipCbs: WeaponEquipEvent[] = [];
  private localFireCbs: VoidEvent[] = [];
  private localKnifeCbs: ((type: 'slash' | 'stab') => void)[] = [];
  private hitCbs: ((msg: HitBroadcastMsg) => void)[] = [];
  private peerFireCbs: ((msg: WeaponFireBroadcastMsg) => void)[] = [];
  private killCbs: ((msg: KillBroadcastMsg) => void)[] = [];
  private matchEndCbs: ((msg: MatchEndBroadcastMsg) => void)[] = [];
  private lockFailedCbs: ((reason: string) => void)[] = [];
  private matchSettledCbs: ((txHash: string) => void)[] = [];
  private settleFailedCbs: ((reason: string) => void)[] = [];
  private flashBlindCbs: ((msg: FlashBlindMsg) => void)[] = [];
  private reconnectingCbs: VoidEvent[] = [];
  private reconnectedCbs: VoidEvent[] = [];
  private reconnectFailedCbs: VoidEvent[] = [];
  private opponentPausedCbs: ((msg: OpponentPausedMsg) => void)[] = [];
  private opponentResumedCbs: VoidEvent[] = [];
  private friendResultCbs: ((msg: FriendResultMsg) => void)[] = [];
  private friendStatusCbs: ((msg: FriendStatusMsg) => void)[] = [];
  /** Session ids the server pushed as already-friends (on join). Cached so a
   *  scoreboard constructed later (after asset load) can seed its badges. */
  private friendSids = new Set<string>();
  private chatCbs: ((msg: ChatBroadcastMsg) => void)[] = [];
  private boundCbs: ((info: { roomId: string; reconnectionToken: string }) => void)[] = [];
  private grenadeStateCbs: ((msg: GrenadeStateBroadcastMsg) => void)[] = [];
  private grenadeDetonateCbs: ((msg: GrenadeDetonateBroadcastMsg) => void)[] = [];
  private grenadeBounceCbs: ((msg: GrenadeBounceBroadcastMsg) => void)[] = [];
  private zoneStateCbs: ((msg: ZoneStateBroadcastMsg) => void)[] = [];
  private groundItemsStateCbs: ((msg: GroundItemsStateBroadcastMsg) => void)[] = [];
  private glassBreakCbs: ((msg: GlassBreakBroadcastMsg) => void)[] = [];
  /**
   * Sprint 7.3 — fires on EVERY snapshot for the local player, even
   * when the position didn't change. The dedup in `onPlayerChange`
   * means stationary-server-side drift never gets reconciled — this
   * callback exists specifically for reconciliation.
   */
  private localSnapshotCbs: PlayerEvent[] = [];
  /**
   * Fires on EVERY snapshot for each REMOTE player (never self), even when
   * nothing changed. Feeds the remote players' interpolation buffer — that
   * feed must be unconditional (a fixed 32 Hz keyframe stream), never
   * subject to the `hasChanged` dedup.
   */
  private remoteSnapshotCbs: PlayerEvent[] = [];

  private known = new Map<string, RemotePlayerState>();
  private lastHud: MatchHudState | null = null;
  private lastPhase = "";

  /* ── Server-tick clock (remote interpolation) ─────────────────────
     Estimates the server's current tick from snapshot arrivals so remote
     players can be rendered a fixed, short interval in the PAST and
     linearly interpolated between two real snapshots (smooth, constant-
     velocity motion) instead of exponentially chasing the newest one. */
  private static readonly TICKS_PER_MS = 64 / 1000;
  /** How many ticks in the past remote players are rendered. Snapshots
   *  arrive every 2 ticks (32 Hz); 5 ticks ≈ 78 ms = 2.5 snapshot
   *  intervals — enough buffer to absorb normal network jitter. */
  private static readonly INTERP_DELAY_TICKS = 5;
  /** EMA of (server tick − local ms × TICKS_PER_MS); null until the
   *  first snapshot (and after a room leave). */
  private clockOffsetTicks: number | null = null;

  /** Colyseus reconnection token for the current room (refreshed on every
   *  bind). Used to rejoin the same seat after an unexpected drop. */
  private reconnectionToken: string | null = null;
  /** True once we left on purpose (disconnect()) — suppresses reconnect. */
  private intentionalLeave = false;
  /** True once the match is decided (match_end) — suppresses reconnect. */
  private matchOver = false;
  /** True once sendClientReady() ran (assets loaded). The actual message
   *  only goes out once `serverLoadGate` confirms the server understands it,
   *  and is repeated on the first flagged snapshot after every (re)bind. */
  private readySent = false;
  /** True once a snapshot carried `loadGate: true` — the server supports the
   *  asset-load handshake. Old servers KICK on unregistered message types
   *  (colyseus WS_CLOSE_WITH_ERROR 4002 in production — mainnet 2026-07-06:
   *  every player was dropped ~3s into matchmaking during the deploy skew),
   *  so `client_ready` must never be sent before this flips. Reset on every
   *  room leave: the next seat may be an older (rolled-back) server. */
  private serverLoadGate = false;

  constructor(serverUrl: string) {
    this.client = new Colyseus.Client(serverUrl);
  }

  async connect(
    playerName?: string,
    walletAddress?: string,
    stakeAmount?: string,
    /** Hupper JWT — the room's onAuth verifies this against JWT_SECRET
     *  and binds the seat to the wallet inside its claims. */
    token?: string,
    /** Public avatar image URL for the scoreboard (the player's uploaded
     *  photo). Relayed verbatim to every peer in the room's snapshot. */
    avatarUrl?: string,
    /** Set for a private friend-vs-friend custom match — seats only the two
     *  invited players (filterBy ["stakeAmount", "customMatchId"]). */
    customMatchId?: string,
    /** Set for a tournament bracket match (`<tournamentId>:<matchId>`) — seats
     *  the two specific opponents; the server skips per-match escrow. */
    tournamentMatchId?: string,
  ): Promise<void> {
    // `stakeAmount` drives the server's filterBy stake-tier matchmaking;
    // `walletAddress` is locked into the HupperGames escrow on join.
    // `readyGateV2` declares support for the asset-load handshake — the
    // server holds the match start until this client sends `client_ready`.
    // V2 (not `readyGate`) on purpose: the v1 server generation waits for
    // `readyGate` declarers but never flags `loadGate` in its snapshots, so
    // this client would defer `client_ready` forever and stall every match
    // at the 30s deadline. A v1 server ignores V2 → instant starts (gate
    // simply off) until the flagged server generation is deployed.
    const room = await this.client.joinOrCreate("fps_arena", {
      name: playerName,
      walletAddress,
      stakeAmount,
      token,
      avatar: avatarUrl,
      customMatchId,
      tournamentMatchId,
      readyGateV2: true,
    });
    console.log(`[GameClient] joined room ${room.roomId} as ${room.sessionId}`);
    this.bindRoom(room);
  }

  /**
   * Join the free practice room (direct play.hupper.fun visitors — no
   * Hupper session, no stake). `practiceId` is a page-generated random id;
   * the in-browser PracticeBot joins with the same one, so the filterBy
   * pairing seats exactly this visitor + their bot. Everything after the
   * join (messages, reconnect surface, prediction) is the same bindRoom
   * pipeline the paid room uses.
   */
  async connectPractice(playerName: string | undefined, practiceId: string): Promise<void> {
    const room = await this.client.joinOrCreate("fps_practice", {
      name: playerName,
      practiceId,
      readyGateV2: true,
    });
    console.log(`[GameClient] joined practice room ${room.roomId} as ${room.sessionId}`);
    this.bindRoom(room);
  }

  /**
   * Wire every room message + lifecycle handler. Called by the initial
   * connect AND by each successful reconnect — the underlying Room
   * instance changes on reconnect, but the subscriber callbacks live on
   * `this`, so re-binding the new room keeps every consumer working.
   */
  private bindRoom(room: Colyseus.Room): void {
    this.room = room;
    this.reconnectionToken = room.reconnectionToken;

    // Surface the room id + fresh reconnection token so the host (web app)
    // can persist it and offer a reconnect card after a page reload.
    for (const cb of this.boundCbs) {
      cb({ roomId: room.roomId, reconnectionToken: room.reconnectionToken });
    }

    room.onMessage("snapshot", (msg: SnapshotMsg) => this.diffSnapshot(msg));

    // Scoreboard ping — echo the server's timestamp straight back so it
    // can measure our round trip and report it in the snapshot.
    room.onMessage("ping", (msg: { t: number }) => room.send("pong", msg));

    room.onMessage("round_end", (msg: RoundEndBroadcastMsg) => {
      for (const cb of this.roundEndCbs) cb(msg);
    });

    room.onMessage("match_end", (msg: MatchEndBroadcastMsg) => {
      this.matchOver = true; // suppress reconnect once the match is decided
      for (const cb of this.matchEndCbs) cb(msg);
    });

    // Outcome of a friend request we sent (scoreboard add-friend button).
    room.onMessage("friend_result", (msg: FriendResultMsg) => {
      for (const cb of this.friendResultCbs) cb(msg);
    });

    // Pre-existing friendship pushed by the server on join — cache it so the
    // scoreboard shows its "Friends" badge on open, even when it's constructed
    // after this message arrives (asset load happens in between).
    room.onMessage("friend_status", (msg: FriendStatusMsg) => {
      if (msg?.friends && msg.targetSessionId) this.friendSids.add(msg.targetSessionId);
      for (const cb of this.friendStatusCbs) cb(msg);
    });

    // In-match chat line — already validated + censored server-side; own
    // messages come back through here too (consistent censoring for both).
    room.onMessage("chat", (msg: ChatBroadcastMsg) => {
      if (typeof msg?.text !== "string" || typeof msg?.name !== "string") return;
      for (const cb of this.chatCbs) cb(msg);
    });

    // Server kicked us because the on-chain stake lock failed (no
    // allowance / balance). Surface so the UI can explain + bounce back.
    room.onMessage("lock_failed", (msg: { reason?: string }) => {
      console.warn(`[GameClient] stake lock failed: ${msg?.reason ?? "unknown"}`);
      for (const cb of this.lockFailedCbs) cb(msg?.reason ?? "stake lock failed");
    });

    // Post-match on-chain settlement outcome (fires after match_end).
    room.onMessage("match_settled", (msg: { txHash?: string }) => {
      for (const cb of this.matchSettledCbs) cb(msg?.txHash ?? "");
    });
    room.onMessage("settle_failed", (msg: { reason?: string }) => {
      console.warn(`[GameClient] settle failed: ${msg?.reason ?? "unknown"}`);
      for (const cb of this.settleFailedCbs) cb(msg?.reason ?? "settlement failed");
    });

    // Server-authoritative flashbang blind (LOS + angle + distance decided
    // server-side; we just render the white-out).
    room.onMessage("flash_blind", (msg: FlashBlindMsg) => {
      for (const cb of this.flashBlindCbs) cb(msg);
    });

    // Reconnect grace — the opponent dropped (opponent_paused) or came
    // back (opponent_resumed). Drives the survivor's "waiting" overlay.
    room.onMessage("opponent_paused", (msg: OpponentPausedMsg) => {
      for (const cb of this.opponentPausedCbs) cb(msg);
    });
    room.onMessage("opponent_resumed", () => {
      for (const cb of this.opponentResumedCbs) cb();
    });

    room.onMessage("weapon_equip", (msg: WeaponEquipBroadcastMsg) => {
      if (typeof msg?.sessionId !== "string" || typeof msg?.weaponId !== "string") return;
      for (const cb of this.weaponEquipCbs) cb(msg);
    });

    room.onMessage("hit", (msg: HitBroadcastMsg) => {
      for (const cb of this.hitCbs) cb(msg);
    });

    room.onMessage("weapon_fire", (msg: WeaponFireBroadcastMsg) => {
      for (const cb of this.peerFireCbs) cb(msg);
    });

    room.onMessage("kill", (msg: KillBroadcastMsg) => {
      for (const cb of this.killCbs) cb(msg);
    });

    room.onMessage("grenade_state", (msg: GrenadeStateBroadcastMsg) => {
      for (const cb of this.grenadeStateCbs) cb(msg);
    });
    room.onMessage("grenade_detonate", (msg: GrenadeDetonateBroadcastMsg) => {
      for (const cb of this.grenadeDetonateCbs) cb(msg);
    });
    room.onMessage("grenade_bounce", (msg: GrenadeBounceBroadcastMsg) => {
      for (const cb of this.grenadeBounceCbs) cb(msg);
    });
    room.onMessage("zone_state", (msg: ZoneStateBroadcastMsg) => {
      for (const cb of this.zoneStateCbs) cb(msg);
    });
    room.onMessage("glass_break", (msg: GlassBreakBroadcastMsg) => {
      for (const cb of this.glassBreakCbs) cb(msg);
    });
    room.onMessage("ground_items_state", (msg: GroundItemsStateBroadcastMsg) => {
      for (const cb of this.groundItemsStateCbs) cb(msg);
    });

    room.onLeave((code: number) => {
      console.log(`[GameClient] left room (code ${code})`);
      // Drop the known-player cache so a reconnect re-bootstraps cleanly.
      for (const [sessionId, snap] of this.known) {
        this.emit(this.removeCbs, snap, sessionId);
      }
      this.known.clear();
      this.lastHud = null;
      this.lastPhase = "";
      // Server-tick clock resyncs from scratch on the next bind — the new
      // room's tick timeline is unrelated to the old one.
      this.clockOffsetTicks = null;
      // Handshake support is per-room: the next seat may live on an older
      // (rolled-back) server. Its first flagged snapshot re-arms this and
      // re-sends the deferred `client_ready` (see diffSnapshot).
      this.serverLoadGate = false;
      // Auto-reconnect on an unexpected drop (network blip). Skip if we
      // left on purpose or the match is already decided.
      if (this.intentionalLeave || this.matchOver) return;
      void this.attemptReconnect();
    });
  }

  /**
   * Rejoin the same seat after an unexpected drop, retrying until just
   * past the server's first-drop grace window (90 s). Fires
   * onReconnecting → onReconnected (success) / onReconnectFailed (gave up).
   */
  private async attemptReconnect(): Promise<void> {
    const token = this.reconnectionToken;
    if (!token) {
      for (const cb of this.reconnectFailedCbs) cb();
      return;
    }
    for (const cb of this.reconnectingCbs) cb();
    const deadline = Date.now() + 95_000;
    let delay = 1000;
    while (Date.now() < deadline) {
      if (this.intentionalLeave || this.matchOver) return;
      try {
        const room = await this.client.reconnect(token);
        this.bindRoom(room);
        console.log(`[GameClient] reconnected as ${room.sessionId}`);
        for (const cb of this.reconnectedCbs) cb();
        return;
      } catch {
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay * 1.5, 5000);
      }
    }
    console.warn(`[GameClient] reconnect window expired`);
    for (const cb of this.reconnectFailedCbs) cb();
  }

  private emit(cbs: PlayerEvent[], snap: RemotePlayerState, sessionId: string): void {
    for (const cb of cbs) cb(snap, sessionId);
  }

  private diffSnapshot(snapshot: SnapshotMsg): void {
    // Server declared the asset-load handshake — only now is `client_ready`
    // safe to send (older servers kick on unregistered message types). If
    // the assets finished before this first snapshot (or before a rebind),
    // flush the deferred report now.
    if (snapshot.loadGate === true && !this.serverLoadGate) {
      this.serverLoadGate = true;
      if (this.readySent) this.room?.send("client_ready", {});
    }

    const prevPhase = this.lastPhase;
    this.lastPhase = snapshot.phase;

    // Advance the server-tick clock estimate. Resync on big jumps
    // (reconnect / long hitch), otherwise smooth network jitter with a
    // light EMA so the interpolation timeline never stutters.
    const measuredOffset =
      snapshot.tick - performance.now() * GameClient.TICKS_PER_MS;
    if (
      this.clockOffsetTicks === null ||
      Math.abs(measuredOffset - this.clockOffsetTicks) > 32
    ) {
      this.clockOffsetTicks = measuredOffset;
    } else {
      this.clockOffsetTicks += (measuredOffset - this.clockOffsetTicks) * 0.1;
    }

    const currentIds = new Set<string>();

    for (const p of snapshot.players) {
      currentIds.add(p.sessionId);
      // Stamp the capture tick on every player entry — the remote
      // interpolation buffer keys its keyframes on it.
      p.tick = snapshot.tick;

      if (!this.known.has(p.sessionId)) {
        this.known.set(p.sessionId, p);
        console.log(`[GameClient] player added: ${p.sessionId} at (${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)})`);
        this.emit(this.addCbs, p, p.sessionId);
      } else {
        const prev = this.known.get(p.sessionId)!;
        if (this.hasChanged(prev, p)) {
          this.known.set(p.sessionId, p);
          this.emit(this.changeCbs, p, p.sessionId);
        }
      }

      // Sprint 7.3 — always fire the local snapshot callback so the
      // FirstPersonController can reconcile its X/Z prediction against
      // the server's authoritative position, even when the server's
      // position hasn't changed between snapshots.
      if (p.sessionId === this.room?.sessionId) {
        // Pass the world-state glass flag down on the local player snapshot
        // so a dropped glass_break event / late join can self-heal the pane.
        p.glassBroken = snapshot.glassBroken;
        for (const cb of this.localSnapshotCbs) cb(p, p.sessionId);
      } else {
        // Unconditional 32 Hz keyframe stream for remote interpolation.
        for (const cb of this.remoteSnapshotCbs) cb(p, p.sessionId);
      }
    }

    for (const sessionId of this.known.keys()) {
      if (!currentIds.has(sessionId)) {
        const removed = this.known.get(sessionId)!;
        this.known.delete(sessionId);
        console.log(`[GameClient] player removed: ${sessionId}`);
        this.emit(this.removeCbs, removed, sessionId);
      }
    }

    const hud: MatchHudState = {
      phase: snapshot.phase,
      ctScore: snapshot.ctScore ?? 0,
      tScore: snapshot.tScore ?? 0,
      roundNumber: snapshot.roundNumber ?? 0,
      roundsToWin: snapshot.roundsToWin ?? 8,
      buySecondsLeft: snapshot.buySecondsLeft,
      buyEndsAtTick: snapshot.buyEndsAtTick,
      liveSecondsLeft: snapshot.liveSecondsLeft,
      introSecondsLeft: snapshot.introSecondsLeft,
      halftimeSecondsLeft: snapshot.halftimeSecondsLeft,
      halftimeAtRound: snapshot.halftimeAtRound,
      halftimeSwapped: snapshot.halftimeSwapped,
    };
    if (
      !this.lastHud ||
      this.lastHud.phase !== hud.phase ||
      this.lastHud.ctScore !== hud.ctScore ||
      this.lastHud.tScore !== hud.tScore ||
      this.lastHud.roundNumber !== hud.roundNumber ||
      this.lastHud.roundsToWin !== hud.roundsToWin ||
      this.lastHud.buySecondsLeft !== hud.buySecondsLeft ||
      this.lastHud.buyEndsAtTick !== hud.buyEndsAtTick ||
      this.lastHud.liveSecondsLeft !== hud.liveSecondsLeft ||
      this.lastHud.introSecondsLeft !== hud.introSecondsLeft ||
      this.lastHud.halftimeSecondsLeft !== hud.halftimeSecondsLeft ||
      this.lastHud.halftimeSwapped !== hud.halftimeSwapped
    ) {
      this.lastHud = hud;
      for (const cb of this.hudCbs) cb(hud);
    }

    // Reposition the local player on a fresh-round transition. Covers the
    // normal between-rounds respawn AND the warmup → buy match start (the
    // server teleports the searching player back from wherever they roamed
    // during warmup to their round-1 spawn — hard-snap there, don't drift).
    // `halftime` sits between between_rounds and buy on the swap round — the
    // side-swap respawn (new spawn point on the OTHER side) must hard-snap
    // when its buy phase begins too.
    if (
      (prevPhase === "between_rounds" ||
        prevPhase === "warmup" ||
        prevPhase === "intro" ||
        prevPhase === "halftime") &&
      snapshot.phase === "buy"
    ) {
      for (const cb of this.liveRoundCbs) cb();
    }
  }

  private hasChanged(a: RemotePlayerState, b: RemotePlayerState): boolean {
    const own = (s: RemotePlayerState) => (s.ownedWeapons ?? []).slice().sort().join(",");
    return (
      a.x !== b.x || a.y !== b.y || a.z !== b.z ||
      a.yaw !== b.yaw || a.pitch !== b.pitch ||
      a.health !== b.health || a.armor !== b.armor || a.helmet !== b.helmet ||
      a.ping !== b.ping || a.kills !== b.kills || a.deaths !== b.deaths ||
      a.weaponId !== b.weaponId ||
      a.lastProcessedInputSeq !== b.lastProcessedInputSeq ||
      a.team !== b.team ||
      a.money !== b.money ||
      a.crouching !== b.crouching ||
      a.walking !== b.walking ||
      a.magAmmo !== b.magAmmo ||
      a.reserveAmmo !== b.reserveAmmo ||
      a.reloading !== b.reloading ||
      a.reloadEndTick !== b.reloadEndTick ||
      a.velocityModifier !== b.velocityModifier ||
      own(a) !== own(b)
    );
  }

  sendInput(frame: InputFrame): void {
    this.room?.send("input", frame);
    if ((frame.actionFlags & ACTION.SHOOT) !== 0) {
      for (const cb of this.localFireCbs) cb();
    }
    if ((frame.actionFlags & ACTION.KNIFE_SLASH) !== 0) {
      for (const cb of this.localKnifeCbs) cb('slash');
    }
    if ((frame.actionFlags & ACTION.KNIFE_STAB) !== 0) {
      for (const cb of this.localKnifeCbs) cb('stab');
    }
  }

  sendBuyWeapon(weaponId: string): void {
    const msg: BuyWeaponMsg = { weaponId };
    this.room?.send("buy_weapon", msg);
  }

  sendWeaponEquip(weaponId: string): void {
    const msg: WeaponEquipMsg = { weaponId };
    this.room?.send("weapon_equip", msg);
  }

  /** File a report against another player (server maps session → wallet). */
  sendReportPlayer(targetSessionId: string, reason: ReportReason, detail?: string): void {
    const msg: ReportPlayerMsg = { targetSessionId, reason, detail };
    this.room?.send("report_player", msg);
  }

  /** Send a friend request to another player (server maps session → wallet). */
  sendFriendRequest(targetSessionId: string): void {
    const msg: FriendRequestMsg = { targetSessionId };
    this.room?.send("friend_request", msg);
  }

  /** Send a chat line (server trims, rate-limits + censors, then broadcasts). */
  sendChat(text: string): void {
    const msg: ChatMsg = { text };
    this.room?.send("chat", msg);
  }

  /** Report every local asset loaded (match-start load gate). The server
   *  holds the match in warmup until both clients have sent this. The wire
   *  send is gated on `serverLoadGate` — a server that never declared the
   *  handshake (older build / rollback) would kick us for it; against one
   *  of those this stays deferred forever and matches start like they
   *  always did. */
  sendClientReady(): void {
    this.readySent = true;
    if (this.serverLoadGate) this.room?.send("client_ready", {});
  }

  /** A chat line arrived (both players', own included). */
  onChat(cb: (msg: ChatBroadcastMsg) => void): () => void {
    this.chatCbs.push(cb);
    return () => { this.chatCbs = this.chatCbs.filter((c) => c !== cb); };
  }

  /** Outcome of a friend request we sent (request sent / now friends / etc.). */
  onFriendResult(cb: (msg: FriendResultMsg) => void): () => void {
    this.friendResultCbs.push(cb);
    return () => { this.friendResultCbs = this.friendResultCbs.filter((c) => c !== cb); };
  }

  /** Session ids the server says we're already friends with (pushed on join). */
  getFriendSids(): ReadonlySet<string> {
    return this.friendSids;
  }

  /** A pre-existing friendship arrived (server push on join). */
  onFriendStatus(cb: (msg: FriendStatusMsg) => void): () => void {
    this.friendStatusCbs.push(cb);
    return () => { this.friendStatusCbs = this.friendStatusCbs.filter((c) => c !== cb); };
  }

  /** Local player's session id (own player). */
  get sessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  /** Latest known snapshot for the local player (after diff). */
  getLatestSelf(): RemotePlayerState | null {
    const sid = this.sessionId;
    if (!sid) return null;
    return this.known.get(sid) ?? null;
  }

  onPlayerAdd(cb: PlayerEvent): () => void {
    this.addCbs.push(cb);
    return () => { this.addCbs = this.addCbs.filter((c) => c !== cb); };
  }
  onPlayerChange(cb: PlayerEvent): () => void {
    this.changeCbs.push(cb);
    return () => { this.changeCbs = this.changeCbs.filter((c) => c !== cb); };
  }
  onPlayerRemove(cb: PlayerEvent): () => void {
    this.removeCbs.push(cb);
    return () => { this.removeCbs = this.removeCbs.filter((c) => c !== cb); };
  }

  /**
   * Sprint 7.3 — every snapshot for the local player. Unlike
   * `onPlayerChange`, this fires even when the server's position
   * didn't change. Used by FirstPersonController.reconcileFromServer
   * to keep local prediction snapped to the server's authoritative
   * X/Z over the lifetime of a stationary moment (when the legit
   * change-dedup would otherwise hide the drift).
   */
  onLocalSnapshot(cb: PlayerEvent): () => void {
    this.localSnapshotCbs.push(cb);
    return () => { this.localSnapshotCbs = this.localSnapshotCbs.filter((c) => c !== cb); };
  }

  /**
   * Every snapshot for each REMOTE player, unconditionally (no change
   * dedup). Feeds `RemotePlayer`'s interpolation buffer with a steady
   * 32 Hz keyframe stream.
   */
  onRemoteSnapshot(cb: PlayerEvent): () => void {
    this.remoteSnapshotCbs.push(cb);
    return () => { this.remoteSnapshotCbs = this.remoteSnapshotCbs.filter((c) => c !== cb); };
  }

  /**
   * The server-tick timeline remote players should be RENDERED at right
   * now — a fixed `INTERP_DELAY_TICKS` behind the estimated live server
   * tick, so there are always two buffered snapshots to interpolate
   * between. Returns null until the first snapshot arrives.
   */
  getRemoteRenderTick(): number | null {
    if (this.clockOffsetTicks === null) return null;
    return (
      performance.now() * GameClient.TICKS_PER_MS +
      this.clockOffsetTicks -
      GameClient.INTERP_DELAY_TICKS
    );
  }

  onMatchHud(cb: MatchHudEvent): () => void {
    this.hudCbs.push(cb);
    return () => { this.hudCbs = this.hudCbs.filter((c) => c !== cb); };
  }

  onRoundEnd(cb: RoundEndEvent): () => void {
    this.roundEndCbs.push(cb);
    return () => { this.roundEndCbs = this.roundEndCbs.filter((c) => c !== cb); };
  }

  /** Fires once when the match is decided — carries the pot payout. */
  onMatchEnd(cb: (msg: MatchEndBroadcastMsg) => void): () => void {
    this.matchEndCbs.push(cb);
    return () => { this.matchEndCbs = this.matchEndCbs.filter((c) => c !== cb); };
  }

  /** Fires if the server rejected our stake lock and is kicking us. */
  onLockFailed(cb: (reason: string) => void): () => void {
    this.lockFailedCbs.push(cb);
    return () => { this.lockFailedCbs = this.lockFailedCbs.filter((c) => c !== cb); };
  }

  /** Fires after the pot was settled on-chain (winner paid). */
  onMatchSettled(cb: (txHash: string) => void): () => void {
    this.matchSettledCbs.push(cb);
    return () => { this.matchSettledCbs = this.matchSettledCbs.filter((c) => c !== cb); };
  }

  /** Fires when the on-chain settlement failed (stakes stay escrowed). */
  onSettleFailed(cb: (reason: string) => void): () => void {
    this.settleFailedCbs.push(cb);
    return () => { this.settleFailedCbs = this.settleFailedCbs.filter((c) => c !== cb); };
  }

  /** Fires when a flashbang blinds the local player (server-authoritative). */
  onFlashBlind(cb: (msg: FlashBlindMsg) => void): () => void {
    this.flashBlindCbs.push(cb);
    return () => { this.flashBlindCbs = this.flashBlindCbs.filter((c) => c !== cb); };
  }

  /** Our connection dropped; a reconnect attempt loop has started. */
  onReconnecting(cb: VoidEvent): () => void {
    this.reconnectingCbs.push(cb);
    return () => { this.reconnectingCbs = this.reconnectingCbs.filter((c) => c !== cb); };
  }

  /** We successfully rejoined our seat after a drop. */
  onReconnected(cb: VoidEvent): () => void {
    this.reconnectedCbs.push(cb);
    return () => { this.reconnectedCbs = this.reconnectedCbs.filter((c) => c !== cb); };
  }

  /** Reconnect window expired without rejoining — the match is lost. */
  onReconnectFailed(cb: VoidEvent): () => void {
    this.reconnectFailedCbs.push(cb);
    return () => { this.reconnectFailedCbs = this.reconnectFailedCbs.filter((c) => c !== cb); };
  }

  /** The opponent dropped; a grace window is open (graceSec, dropCount). */
  onOpponentPaused(cb: (msg: OpponentPausedMsg) => void): () => void {
    this.opponentPausedCbs.push(cb);
    return () => { this.opponentPausedCbs = this.opponentPausedCbs.filter((c) => c !== cb); };
  }

  /** The opponent reconnected within the grace window. */
  onOpponentResumed(cb: VoidEvent): () => void {
    this.opponentResumedCbs.push(cb);
    return () => { this.opponentResumedCbs = this.opponentResumedCbs.filter((c) => c !== cb); };
  }

  /** Fires on every room bind (join + reconnect) with the current room id
   *  and reconnection token. Host persists it for the reconnect card. */
  onBound(cb: (info: { roomId: string; reconnectionToken: string }) => void): () => void {
    this.boundCbs.push(cb);
    return () => { this.boundCbs = this.boundCbs.filter((c) => c !== cb); };
  }

  /** Rejoin a specific seat by its reconnection token — used by the web
   *  reconnect card after a page reload. Clears the terminal flags so the
   *  freshly-bound room behaves like a live connection again. */
  async reconnectWith(token: string): Promise<void> {
    this.intentionalLeave = false;
    this.matchOver = false;
    const room = await this.client.reconnect(token);
    this.bindRoom(room);
    console.log(`[GameClient] reconnected (card) as ${room.sessionId}`);
  }

  /** A fresh round's buy phase begins — fired on `between_rounds → buy` and
   *  on the `warmup → buy` match start. Subscribers re-sync local positions. */
  onLiveRoundStarted(cb: VoidEvent): () => void {
    this.liveRoundCbs.push(cb);
    return () => { this.liveRoundCbs = this.liveRoundCbs.filter((c) => c !== cb); };
  }

  onLocalFire(cb: VoidEvent): () => void {
    this.localFireCbs.push(cb);
    return () => { this.localFireCbs = this.localFireCbs.filter((c) => c !== cb); };
  }

  onLocalKnifeAttack(cb: (type: 'slash' | 'stab') => void): () => void {
    this.localKnifeCbs.push(cb);
    return () => { this.localKnifeCbs = this.localKnifeCbs.filter((c) => c !== cb); };
  }

  onHit(cb: (msg: HitBroadcastMsg) => void): () => void {
    this.hitCbs.push(cb);
    return () => { this.hitCbs = this.hitCbs.filter((c) => c !== cb); };
  }

  /**
   * Sprint 5 — fires once per authoritative shot fired by *any* player
   * (hit or miss). Subscribers render bullet tracers + wall-impact dust.
   */
  onPeerFire(cb: (msg: WeaponFireBroadcastMsg) => void): () => void {
    this.peerFireCbs.push(cb);
    return () => { this.peerFireCbs = this.peerFireCbs.filter((c) => c !== cb); };
  }

  /** Sprint 6 — fires once per authoritative kill. Drives the kill feed. */
  onKill(cb: (msg: KillBroadcastMsg) => void): () => void {
    this.killCbs.push(cb);
    return () => { this.killCbs = this.killCbs.filter((c) => c !== cb); };
  }

  /** Sprint 7 — per-tick snapshot of in-flight grenades for peer rendering. */
  onGrenadeState(cb: (msg: GrenadeStateBroadcastMsg) => void): () => void {
    this.grenadeStateCbs.push(cb);
    return () => { this.grenadeStateCbs = this.grenadeStateCbs.filter((c) => c !== cb); };
  }

  /** Sprint 7 — one-shot on grenade impact; drives the matching effect. */
  onGrenadeDetonate(cb: (msg: GrenadeDetonateBroadcastMsg) => void): () => void {
    this.grenadeDetonateCbs.push(cb);
    return () => { this.grenadeDetonateCbs = this.grenadeDetonateCbs.filter((c) => c !== cb); };
  }

  /** One-shot on an audible grenade bounce; drives the impact ("hit") sound. */
  onGrenadeBounce(cb: (msg: GrenadeBounceBroadcastMsg) => void): () => void {
    this.grenadeBounceCbs.push(cb);
    return () => { this.grenadeBounceCbs = this.grenadeBounceCbs.filter((c) => c !== cb); };
  }

  /** Sprint 7 — per-tick snapshot of active smoke + fire zones. */
  onZoneState(cb: (msg: ZoneStateBroadcastMsg) => void): () => void {
    this.zoneStateCbs.push(cb);
    return () => { this.zoneStateCbs = this.zoneStateCbs.filter((c) => c !== cb); };
  }

  /** Fires once when the breakable glass pane shatters (server-authoritative). */
  onGlassBreak(cb: (msg: GlassBreakBroadcastMsg) => void): () => void {
    this.glassBreakCbs.push(cb);
    return () => { this.glassBreakCbs = this.glassBreakCbs.filter((c) => c !== cb); };
  }

  /** Sprint 7 — client → server grenade throw. */
  sendGrenadeThrow(msg: GrenadeThrowMsg): void {
    this.room?.send("grenade_throw", msg);
  }

  // ── Drop / pickup (CS:GO weapon swap) ──────────────────────────────
  sendDropWeapon(weaponId?: string): void {
    const msg: DropWeaponMsg = weaponId ? { weaponId } : {};
    this.room?.send("drop_weapon", msg);
  }
  sendPickupWeapon(): void {
    const msg: PickupWeaponMsg = {};
    this.room?.send("pickup_weapon", msg);
  }
  onGroundItemsState(cb: (msg: GroundItemsStateBroadcastMsg) => void): () => void {
    this.groundItemsStateCbs.push(cb);
    return () => { this.groundItemsStateCbs = this.groundItemsStateCbs.filter((c) => c !== cb); };
  }

  /** Lookup the cached snapshot for any session. Useful for effect modules. */
  getPlayerSnapshot(sessionId: string): RemotePlayerState | null {
    return this.known.get(sessionId) ?? null;
  }

  /** Iterate every cached player (including self). Used by SpectatorCam. */
  forEachKnownPlayer(cb: (snap: RemotePlayerState) => void): void {
    for (const snap of this.known.values()) cb(snap);
  }

  /**
   * Sprint 7.2 — current authoritative match phase. Returns `null`
   * until the first snapshot arrives. Used by FirstPersonController to
   * freezetime-lock movement during the buy phase.
   */
  getMatchPhase(): string | null {
    return this.lastHud?.phase ?? null;
  }

  onWeaponEquip(cb: WeaponEquipEvent): () => void {
    this.weaponEquipCbs.push(cb);
    return () => { this.weaponEquipCbs = this.weaponEquipCbs.filter((c) => c !== cb); };
  }

  async disconnect(): Promise<void> {
    this.intentionalLeave = true; // suppress the auto-reconnect in onLeave
    if (this.room) {
      await this.room.leave();
      this.room = null;
    }
  }
}
